import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { updateSubscriptionStatus } from "@/api/auth";
import appLogo from "@/images/notanaicon.svg";
import { useToast } from "@/hooks/useToast";
import { useAuth, SubscriptionStatus } from "@/contexts/AuthContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { X, Check } from "lucide-react";

export function SubscriptionChoice() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { subscription, checkSubscriptionStatus } = useAuth();
  const from = location.state?.from?.pathname || "/app/meetings";
  
  // Check if user is changing their subscription (vs. initial selection after trial)
  const isChangingSubscription = location.search.includes('change=true');
  
  // Pre-select current subscription status if changing plans
  const [selectedOption, setSelectedOption] = useState<SubscriptionStatus | null>(
    isChangingSubscription ? subscription?.status || null : null
  );
  
  const handleContinue = async () => {
    if (!selectedOption) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a subscription option"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Update subscription status
      await updateSubscriptionStatus(selectedOption);
      
      // Refresh subscription status with force refresh parameter
      await checkSubscriptionStatus(true);
      
      toast({
        title: "Success",
        description: selectedOption === 'paid' 
          ? "Thank you for subscribing to Notana Premium!" 
          : "You're now on the free tier"
      });
      
      // Force a complete page reload to ensure clean state
      // Use window.location instead of navigate to force a full page reload
      window.location.href = from;
    } catch (error) {
      console.error("Failed to update subscription:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred. Please try again."
      });
      setIsSubmitting(false);
    }
    // Don't call setIsSubmitting(false) for the success case since we're reloading the page
  };

  // If subscription data is not loaded yet, show loading spinner
  if (!subscription) {
    return <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner />
    </div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-4">
      {/* Animated Background Gradient Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient layer */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-slate-100 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950"></div>
        
        <div className="absolute top-0 -left-40 w-96 h-96 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob" />
        <div className="absolute top-0 -right-40 w-96 h-96 bg-gradient-to-r from-indigo-400 to-violet-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-r from-violet-400 to-purple-400 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 dark:opacity-30 animate-blob animation-delay-4000" />
      </div>

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii43NSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoMzAwdjMwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-40 dark:opacity-20 pointer-events-none" />

      <Card className="w-full max-w-4xl relative z-10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src={appLogo} alt="Notana Logo" className="h-8 w-8 dark:invert" />
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Choose Your Notana Plan</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center mb-6">
            {isChangingSubscription ? (
              <>
                <h2 className="text-2xl font-semibold mb-2">Manage Your Subscription</h2>
                <p className="text-muted-foreground">
                  You're currently on the <strong>{subscription.status}</strong> plan.
                  {subscription.status === 'trial' && subscription.daysRemaining > 0 && 
                    ` Your trial will expire on ${subscription.trialExpiresOn?.toLocaleDateString()}.`}
                </p>
                <p className="mt-2 text-muted-foreground">
                  You can change your subscription at any time:
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold mb-2">Your 30-day free trial has ended</h2>
                <p className="text-muted-foreground">
                  Thank you for trying Notana! Your trial period that started on {subscription.trialExpiresOn ? new Date(new Date(subscription.trialExpiresOn).getTime() - 30*24*60*60*1000).toLocaleDateString() : '(30 days ago)'} has now expired.
                </p>
                <p className="mt-2 text-muted-foreground">
                  Please select which plan you'd like to continue with:
                </p>
              </>
            )}
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Tier Option */}
            <div 
              className={`border rounded-lg p-6 cursor-pointer transition-all ${
                selectedOption === 'free' 
                  ? 'border-indigo-500 ring-2 ring-indigo-500/20 shadow-lg shadow-indigo-500/20' 
                  : 'hover:border-primary/50'
              }`}
              onClick={() => setSelectedOption('free')}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-medium">Free Tier</h3>
                  <p className="text-muted-foreground">Limited access</p>
                </div>
                <div className="text-2xl font-bold">£0</div>
              </div>
              
              <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-2">
                  <X className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">Cannot create new meetings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Read-only access to existing meetings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>View meetings shared by other users</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Update status of shared actions</span>
                </li>
              </ul>
              
              <Button 
                variant={selectedOption === 'free' ? "default" : "outline"} 
                className="w-full"
                onClick={() => setSelectedOption('free')}
              >
                Select Free Plan
              </Button>
            </div>
            
            {/* Paid Tier Option */}
            <div 
              className={`border rounded-lg p-6 cursor-pointer transition-all ${
                selectedOption === 'paid' 
                  ? 'border-indigo-500 ring-2 ring-indigo-500/20 shadow-lg shadow-indigo-500/20 bg-gradient-to-br from-indigo-500/5 to-violet-500/5' 
                  : 'hover:border-primary/50'
              }`}
              onClick={() => setSelectedOption('paid')}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-medium">Premium</h3>
                  <p className="text-muted-foreground">Full access</p>
                </div>
                <div className="text-2xl font-bold">£5<span className="text-sm text-muted-foreground font-normal">/month</span></div>
              </div>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Create unlimited new meetings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Full access all existing meetings</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>View meetings shared by other users</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Update status of shared actions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                  <span>Share meetings with anybody</span>
                </li>

              </ul>
              
              <Button 
                variant={selectedOption === 'paid' ? "default" : "outline"} 
                className="w-full"
                onClick={() => setSelectedOption('paid')}
              >
                Select Premium Plan
              </Button>
            </div>
          </div>
          
          <div className="bg-muted p-4 rounded-md mt-6">
            <div className="flex items-start gap-3">
              <div>
                <h4 className="font-medium">Note</h4>
                <p className="text-sm text-muted-foreground">
                  This is a trial version of our subscription system. No payment details will be collected at this stage.
                  We're simply tracking your subscription preference for feature access.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-4">
          {isChangingSubscription && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/app/meetings')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button 
            onClick={handleContinue} 
            disabled={isSubmitting || !selectedOption}
            size="lg"
            className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/40"
          >
            {isSubmitting ? "Processing..." : isChangingSubscription ? "Save Changes" : "Continue"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
